/**
 * The one shared, app-lifetime cache of `RoleSnapshot`s (owner requirement
 * 2026-09-26, requirement 1: "switching organism / panel / screen is a
 * client route change with shared app state (one store) ... loaded and
 * decoded data survives every click").
 *
 * Without this, navigating away from an organism screen and back would
 * construct a brand-new `RoleSnapshot`/`ArtifactStore`, throwing away the
 * in-memory L1 LRU (`src/cache/LruCache.ts`) even though IndexedDB (L2)
 * would still serve the bytes — this registry keeps the *whole* object
 * graph alive for the page's lifetime instead, so a return visit to a
 * screen is instant with no async cache lookup at all.
 *
 * Explicitly a plain in-memory `Map`, not `sessionStorage`/`localStorage`:
 * `RoleSnapshot` holds live `ArtifactStore` instances (with their own LRU
 * and pending promises), which are not serializable — this cache is scoped
 * to one page session by construction, and a hard reload legitimately
 * starts fresh in memory (falling back to the IndexedDB L2, then network).
 */

import { loadRoleSnapshot, type RoleLocation, type RoleSnapshot } from "./snapshotIndex";

const pending = new Map<string, Promise<RoleSnapshot | null>>();

function cacheKey(location: RoleLocation): string {
  return `${location.path}`;
}

/** Loads (once) or returns the already-loaded `RoleSnapshot` for `location`.
 * Concurrent calls for the same role while the first load is in flight
 * share the same promise rather than issuing duplicate network requests. */
export function getRoleSnapshot(location: RoleLocation): Promise<RoleSnapshot | null> {
  const key = cacheKey(location);
  const existing = pending.get(key);
  if (existing) return existing;
  const promise = loadRoleSnapshot(location);
  pending.set(key, promise);
  // A failed load must not poison the cache forever — the next screen visit
  // should retry rather than replay the same rejection indefinitely.
  promise.catch(() => pending.delete(key));
  return promise;
}

/** Drops one role's cached snapshot, forcing the next `getRoleSnapshot` call
 * to reload — used when a background revalidation detects `cursor_seq` (or
 * the v1 `version`) moved and the panels need a genuinely fresh object. */
export function invalidateRoleSnapshot(location: RoleLocation): void {
  pending.delete(cacheKey(location));
}

/** Test-only: clears every cached role (avoids cross-test leakage). */
export function resetRoleSnapshotRegistryForTests(): void {
  pending.clear();
}
